import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { UnsavedWorkService } from '@shared/services/unsaved-work.service';
import { catchError, delay, filter, map, switchMap, tap } from 'rxjs/operators';
import { AmplitudeService, JwtDecoderService, LanguageService, Mapper, UserDeviceService } from '@core/services';
import { BaseAPI } from '@core/services';
import { Router } from '@angular/router';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { GeneralResponse, JWTTokenValidation } from '@shared/enums/global.enum';
import { filterNullEntity, LocalStorageUtil } from '@shared/utils';
import {
  AmplitudeEvent,
  AmplitudeLoginMethod,
  APIConstants,
  ConfigConstant,
} from '@shared/constants';
import {
  AccountVerificationType,
  ResetPasswordMethods,
  ShippingType,
  UserType,
} from '@shared-features/enums';
import { RoutesUtil } from '../utils/routes.util';
import { UserParam } from '@features/users/models/user-param.model';
import { StorageConstant } from '@shared/constants/storage.constant';
import { User } from '@features/users/models/user.model';
import { UserDevice } from '@features/users/models/user-device.model';
import { PasswordResetType } from '@shared/enums/password-reset-type.enum';
import { AsyncFeedbackService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { RecaptchaVersion } from '@shared/enums/recaptcha-version.enum';
import { OtpResponse } from '@shared/models/otp-response.model';

@Injectable({
  providedIn: 'root',
})
export class UserApi {
  public currentUser$: BehaviorSubject<User> = new BehaviorSubject(null);
  public redirectionURL = '';

  constructor(
    private baseAPI: BaseAPI,
    private mapper: Mapper,
    private userDeviceService: UserDeviceService,
    private jwtDecoderService: JwtDecoderService,
    private router: Router,
    private refreshTokenHandler: HttpBackend,
    protected asyncFeedbackService: AsyncFeedbackService,
    private translateService: TranslateService,
    private amplitudeService: AmplitudeService,
    private unsavedWorkService: UnsavedWorkService,
    private languageService: LanguageService
  ) {
    // Restore the current user whenever a token exists (Valid OR Expired).
    // An expired access token is still decodable and TokenInterceptor will
    // refresh it on the first request; populating roles here prevents route
    // guards from wrongly redirecting to access-denied on a fresh tab.
    if (this.jwtDecoderService.isThereValidToken() !== JWTTokenValidation.NotFound) {
      this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
    }

    // Re-register on a language switch so pushes arrive in the new language; the token is unchanged.
    // On startup, only when the stored device's language is stale (e.g. registered with the old hard-coded 'en').
    const staleOnStartup$ = of(null).pipe(delay(0), filter(() => this.isLoggedIn() && this.isDeviceLanguageStale()));
    merge(staleOnStartup$, this.languageService.languageChanged)
      .pipe(
        switchMap(() =>
          this.registerDevice(this.userDeviceService.getCurrentUserDevice()?.notificationToken || null).pipe(catchError(() => of(null)))
        )
      )
      .subscribe();
  }

  private isDeviceLanguageStale(): boolean {
    const userDevice = this.userDeviceService.getCurrentUserDevice();
    return !!userDevice && userDevice.languageIsoCode !== this.userDeviceService.currentLanguageIsoCode();
  }

  changePassword(body: { currentPassword: string, newPassword: string }): Observable<String> {
    return this.baseAPI.post(APIConstants.API_CHANGE_PASSWORD, body);
  }

  registerDevice(notificationToken: string): Observable<GeneralResponse> {
    let userDevice = this.userDeviceService.getCurrentUserDevice();
    if (!userDevice) {
      userDevice = this.userDeviceService.generateNewDevice();
    }
    userDevice.notificationToken = notificationToken;
    // A stored device keeps the language it was first registered with, so refresh it on every call.
    userDevice.languageIsoCode = this.userDeviceService.currentLanguageIsoCode();
    this.userDeviceService.saveUserDevice(userDevice);

    const timezoneOffset = -1 * new Date().getTimezoneOffset();
    const body = {
      appVersion: userDevice.appVersion,
      osType: userDevice.osType,
      deviceId: userDevice.deviceId,
      timezone: timezoneOffset,
      languageIsoCode: userDevice.languageIsoCode,
    };

    if (userDevice.notificationToken) {
      body['notificationToken'] = userDevice.notificationToken;
    }

    if (userDevice.osVersion) {
      body['osVersion'] = userDevice.osVersion;
    }

    if (userDevice.deviceModel) {
      body['deviceModel'] = userDevice.deviceModel;
    }
    return this.baseAPI.post(APIConstants.API_REGISTER_DEVICE, body);
  }

  login(email: string, password: string, otp?: number): Observable<User> {
    const userDevice = this.userDeviceService.getCurrentUserDevice();
    const body = {
      username: email,
      password: password,
      deviceId: userDevice?.deviceId,
      platform: userDevice?.osType || 'WEB',
      app: ConfigConstant.LOGIN_APP,
      otp: otp,
    };
    return this.baseAPI.post(APIConstants.API_LOGIN, body).pipe(
      map((res) => {
        const user: User = this.mapper.fromJson(User, res.data);
        this.saveLoginData(user)
        return user;
      })
    );
  }

  saveLoginData(user: User, method: AmplitudeLoginMethod = AmplitudeLoginMethod.Email) {
    this.jwtDecoderService.saveToken(user.toJson());
    this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
    this.trackLogin(method);
  }

  /**
   * Sends the agreed user properties to Amplitude on every sign-in. Reads back
   * from `currentUser$` because the token payload is the fuller user object.
   * Identify runs before the event so it carries the global user properties.
   */
  private trackLogin(method: AmplitudeLoginMethod): void {
    const user = this.currentUser$.value;
    this.amplitudeService.init(user);
    this.amplitudeService.identify(user);
    this.amplitudeService.track(AmplitudeEvent.USER_LOGGED_IN, { method });
  }

  loginWithPhone(mobileNumber: string, otpCode: string): Observable<User> {
    const userDevice = this.userDeviceService.getCurrentUserDevice();
    const body = {
      deviceId: userDevice?.deviceId,
      platform: userDevice?.osType || 'WEB',
      app: ConfigConstant.LOGIN_APP,
      otpCode,
      mobileNumber,
    };
    return this.baseAPI.post(APIConstants.LOGIN_WITH_PHONE, body).pipe(
      map((res) => {
        const user: User = this.mapper.fromJson(User, res.data);
        this.jwtDecoderService.saveToken(user.toJson());
        this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
        this.trackLogin(AmplitudeLoginMethod.Otp);
        return user;
      })
    );
  }

  refreshTheToken(): Observable<User> {
    const http = new HttpClient(this.refreshTokenHandler);
    const currentUser = this.jwtDecoderService.getCurrentUserFromJWTToken();
    const userDevice = this.userDeviceService.getCurrentUserDevice();

    // `UDK` can be lost while the tokens survive (a version bump clears localStorage), and the
    // backend then rejects the refresh with "Device id is required". The token carries the same
    // deviceId, so fall back to it rather than sending nothing.
    const deviceId = userDevice?.deviceId || this.jwtDecoderService.getDeviceIdFromToken(currentUser?.accessToken);

    const body = {
      deviceId,
      platform: userDevice?.osType || 'WEB',
      app: ConfigConstant.LOGIN_APP,
    };

    const options = {
      headers: this.baseAPI
        .getHeaders()
        .append('Authorization', 'Bearer ' + currentUser?.refreshToken),
    };

    return http.post<any>(this.baseAPI.switchUrlBase(APIConstants.API_REFRESH_TOKEN), body, options).pipe(
      map((res) => {
        const user: User = this.mapper.fromJson(User, res.data);
        this.setCurrentUser(user);
        return user;
      })
    );
  }

  setCurrentUser(user: User): User {
    this.jwtDecoderService.saveToken(user.toJson());
    this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
    return user;
  }

  isLoggedIn(): boolean {
    return this.jwtDecoderService.isThereValidToken() !== JWTTokenValidation.NotFound;
  }

  verifyEmail(
    token: string,
    password: string,
    verificationType: AccountVerificationType
  ): Observable<GeneralResponse> {
    const body = {
      token,
      password,
      verificationType,
    };

    return this.baseAPI.post(APIConstants.API_VERIFY_EMAIL, body).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  getAllUsers(userType?: UserType, id?: number, shippingType?: ShippingType): Observable<User[]> {
    const params = filterNullEntity({
      customerId: userType === UserType.CUSTOMER ? id?.toString() : null,
      fleetId: userType === UserType.FLEET ? id?.toString() : null,
      shippingType,
    });

    return this.baseAPI.get(APIConstants.API_GET_ALL_USERS, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(User, res.data);
      })
    );
  }

  getUserById(userId: string): Observable<User> {
    return this.baseAPI.get(APIConstants.API_GET_USER_BY_ID + userId).pipe(
      map((res) => {
        return this.mapper.fromJson(User, res.data);
      })
    );
  }

  addUser(
    user: User,
    userType: UserType = UserType.CUSTOMER,
    id?: number,
    roles: number[] = []
  ): Observable<User> {
    const body = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobileNumber: user.mobileNumber,
      countryId: user.country?.id,
      roleIds: roles,
      userType: userType,
      shippingType: user.shippingType,
      allowToLogin: user.allowToLogin,
      params: user.params || [],
      id: id,
    };

    return this.baseAPI.post(APIConstants.API_ADD_USER, body).pipe(
      map((res) => {
        return this.mapper.fromJson(User, res.data);
      })
    );
  }

  updateUser(user: Partial<User> & { countryId: number; roleIds: number[] }): Observable<User> {
    return this.baseAPI.put(APIConstants.API_UPDATE_USER, user).pipe(
      map((res) => {
        this.currentUser$.value.firstName = res.data.firstName;
        this.currentUser$.value.lastName = res.data.lastName;
        this.setCurrentUser(this.currentUser$.value);
        const updatedUser = this.mapper.fromJson(User, res.data);
        this.amplitudeService.identify(updatedUser);
        return updatedUser;
      },
    )
    );
  }

  resetPassword(id: number, type: UserType, password?: string): Observable<string> {
    const body: { id: number; type: UserType; password?: string } = { id, type };
    if (type === UserType.DRIVER && password) {
      body.password = password;
    }
    return this.baseAPI.post(APIConstants.API_RESET_PASSWORD, body);
  }

  logout(redirectToReset = false, showMessage = true): Observable<string> {
    // Unsaved work is persisted BEFORE the logout call, which can invalidate the session server-side.
    // Skipped when `redirectToReset`, which is only ever the 428 password-expired path.
    const saveUnsavedWork$ =
      (redirectToReset || !this.isLoggedIn()) ? of(null) : this.unsavedWorkService.saveAll();

    return saveUnsavedWork$.pipe(
      switchMap(() => this.logoutRequest()),
      tap((res) => {
        this.resetUser();
        const targetRoute = redirectToReset
          ? [RoutesUtil.AuthResetPasswordBy.url()]
          : [RoutesUtil.AuthLogin.url()];
        const queryParams = redirectToReset
          ? {
              queryParams: { resetType: PasswordResetType.Reset },
            }
          : {};
        if (showMessage) {
          this.asyncFeedbackService.showSuccessMessage(res);
        }
        this.router.navigate(targetRoute, queryParams);
      }),
      catchError(() => {
        const failureMsg = this.translateService.instant('sharedFeatures.FailedToLogout');
        this.asyncFeedbackService.showFailureMessage(failureMsg);
        return of(failureMsg);
      })
    );
  }

  logoutRequest(): Observable<string> {
    return this.baseAPI.post(APIConstants.LOGOUT, {}, {}).pipe(map((res) => res.data));
  }

  resetUser(): void {
    // Track before reset(), which clears the user and device ids.
    this.amplitudeService.track(AmplitudeEvent.USER_LOGGED_OUT);
    this.amplitudeService.reset();
    this.jwtDecoderService.removeCurrentToken();
    LocalStorageUtil.removeItem(StorageConstant.LOCAL_STORAGE_DASHBOARD_STATUS);
    LocalStorageUtil.removeItem(StorageConstant.COLUMN_OPTIONS_KEY);
    this.currentUser$.next(null);
  }

  customResetPassword(
    id: string,
    type: UserType,
    password: string,
    newPassword: string
  ): Observable<string> {
    const body = { id, type, password, newPassword };
    return this.baseAPI.post(APIConstants.API_CUSTOM_RESET_PASSWORD, body);
  }

  forgetPassword(
    type: ResetPasswordMethods,
    value: string,
    recaptchaToken: string,
    recaptchaVersion: RecaptchaVersion = RecaptchaVersion.V3
  ): Observable<OtpResponse> {
    const idKeyName = type === ResetPasswordMethods.EMAIL ? 'email' : 'mobileNumber';
    const body = { type, [idKeyName]: value };
    let headers = this.baseAPI.getHeaders();
    headers = recaptchaVersion === RecaptchaVersion.V2
      ? headers.append('recaptcha-v2', recaptchaToken)
      : headers.append('recaptcha', recaptchaToken);
    const options = { headers: headers };

    return this.baseAPI.post(APIConstants.FORGET_PASSWORD, body, options).pipe(
      map((res) => this.mapper.fromJson(OtpResponse, res.data))
    );
  }

  forgetPasswordVerifyMobile(otp: string, mobileNumber: string): Observable<string> {
    const body = { otp, mobileNumber };
    return this.baseAPI.post(APIConstants.FORGET_PASSWORD_VERIFY_MOBILE, body).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  getUserParams(userId: number): Observable<UserParam[]> {
    return this.baseAPI
      .get(APIConstants.GET_USER_PARAMS, { params: filterNullEntity({ userId }) })
      .pipe(map((res) => this.mapper.fromJson(UserParam, res.data)));
  }

  saveUserParams(userId: number, params: UserParam[]): Observable<UserParam[]> {
    return this.baseAPI
      .post(APIConstants.ADD_EDIT_PARAMS, filterNullEntity({ userId, params }))
      .pipe(map((res) => this.mapper.fromJson(UserParam, res.data)));
  }

  reSaveUserInStorage(user: User) {
    this.jwtDecoderService.removeCurrentToken();
    this.jwtDecoderService.saveToken(user.toJson());
    this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
    this.amplitudeService.identify(this.currentUser$.value);
  }
}
